import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';

import { AppState } from '.';
import {
  exceptionOf,
  Feature,
  Operator,
  Query,
  SerializedException,
  Subscription,
  SubscriptionSource,
} from '../models';
import { SubscriptionService } from '../services';
import { SubscriptionStatus } from '../models/subscriptionStatus';

export const SUBSCRIPTION_FEATURE_KEY = 'subscription';

const subscriptionAdapter = createEntityAdapter<Subscription>();

interface SubscriptionState extends EntityState<Subscription> {
  // Indicates if the user had any subscription before
  hadSubscription: boolean;
  hadPaidSubscription: boolean;
  loading: boolean;
  errors: SerializedException[];
}

const createInitialState = (): SubscriptionState => subscriptionAdapter.getInitialState({
  hadSubscription: false,
  hadPaidSubscription: false,
  loading: true,
  errors: [],
});

export const doFetchSubscriptions = createAsyncThunk(
  'subscription/fetchSubscription',
  async (input: { accountId: number }) => {
    const { accountId } = input;
    const criteria: Query<Subscription>[] = [
      {
        field: 'accountId',
        operator: Operator.EQ,
        value: accountId,
      },
      {
        field: 'status',
        operator: Operator.EQ,
        value: SubscriptionStatus.ACTIVE,
      },
    ];
    const subscriptionResponse = await SubscriptionService.getSubscriptions(...criteria);
    return { subscriptionResponse };
  },
);

export const doToggleAutoRenew = createAsyncThunk(
  'subscriptions/toggleAutoRenew',
  async (recurlySubscriptionId: number, { rejectWithValue }) => {
    try {
      const autoRenew = await SubscriptionService.toggleAutoRenew(recurlySubscriptionId);
      return autoRenew;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const subscriptionSlice = createSlice({
  name: SUBSCRIPTION_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(doFetchSubscriptions.pending, state => {
        state.loading = true;
      })
      .addCase(doFetchSubscriptions.fulfilled, (state, action) => {
        state.hadSubscription = action.payload.subscriptionResponse.length > 0;
        state.hadPaidSubscription = action.payload.subscriptionResponse.filter(
          sub => sub.source === SubscriptionSource.RECURLY,
        ).length > 0;

        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const subscriptions = action.payload.subscriptionResponse.filter(
          sub => sub.status === SubscriptionStatus.ACTIVE
            && sub.archived === false
            && sub.expiryDate > sevenDaysAgo,
        );
        subscriptionAdapter.setAll(state, subscriptions);
        state.loading = false;
      })
      .addCase(doFetchSubscriptions.rejected, (state, action) => {
        const payload = action.payload as SerializedException;
        state.loading = false;
        state.errors.push(payload);
      })
      .addCase(doToggleAutoRenew.pending, state => {
        state.loading = true;
      })
      .addCase(doToggleAutoRenew.fulfilled, state => {
        state.loading = false;
      })
      .addCase(doToggleAutoRenew.rejected, (state, action) => {
        state.loading = false;
        state.errors.push(action.payload as SerializedException);
      });
  },
});

const selectSubscriptionFeature = (state: AppState) => state[SUBSCRIPTION_FEATURE_KEY];
const { selectAll } = subscriptionAdapter.getSelectors(selectSubscriptionFeature);

export const selectLoading = createSelector(
  selectSubscriptionFeature,
  subState => subState.loading,
);

export const selectErrors = createSelector(
  selectSubscriptionFeature,
  subState => subState.errors,
);

export const selectByAccountId = (accountId: number) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(it => it.accountId === accountId),
);

export const selectSubscriptionsBySource = (
  source: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions
    .filter(it => it.source === source),
);

const isTrueSubscription = (subscription: Subscription, source?: SubscriptionSource) => (
  (!source || subscription.source === source)
  && (
    subscription.feature === Feature.TRUE_TEAM
    || subscription.feature === Feature.TRUE_ENTERPRISE
    || subscription.feature === Feature.TRUE_PLATFORM_ONLY
    || subscription.feature === Feature.TRUE_AUTOMATION
  )
);

export const selectTrueSubscriptionList = (
  source?: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions.filter(subscription => isTrueSubscription(subscription, source)),
);

export const selectTrueSubscriptions = (
  source?: SubscriptionSource,
) => createSelector(
  selectAll,
  subscriptions => subscriptions.find(subscription => isTrueSubscription(subscription, source)),
);

export const hasPaidSubscription = (accountId: number) => createSelector(
  selectAll,
  subscriptions => subscriptions.some(it => it.accountId === accountId
    && it.source === SubscriptionSource.RECURLY),
);

// export const {} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
